import type { MapObject } from '@/features/data-stream/types';
import { altitudeToColor } from '@/lib/aircraft/altitudeColor';
import { renderAircraftMarker } from '@/lib/aircraft/tar1090Adapter';

/** Build a tar1090 aircraft marker element from a MapObject. `dim` desaturates
 *  (stale). Returns the element + whether rotation should be skipped. */
export function buildAircraftMarkerEl(
  obj: MapObject,
  dim: boolean,
): { el: HTMLElement; noRotate: boolean } {
  const category = (obj.fields.category as string | undefined) ?? null;
  const fill = altitudeToColor(obj.altitudeFt ?? null, { stale: dim });
  const { svg, noRotate, width, height } = renderAircraftMarker({
    category,
    type: null,
    altitudeFt: obj.altitudeFt ?? null,
    fill,
    scale: 1,
  });
  // Fixed square box (sqrt(2)-scaled so a 45°-rotated icon still fits) with the
  // SVG in an absolutely-centered child — this keeps the element's bbox centred
  // on the geo point under `anchor:'center'`, so the hover tooltip's pin
  // (left:50%) lines up exactly with the icon. `position:absolute` (not
  // relative) is required: Mapbox positions markers purely via an inline
  // transform and needs them out of normal flow — `relative` would override
  // the `.mapboxgl-marker` rule and make markers drift on zoom. The sized,
  // absolutely-positioned box still serves as the containing block for the SVG.
  const maxDim = Math.ceil(Math.max(width, height) * Math.SQRT2);
  const el = document.createElement('div');
  el.style.cssText = `position:absolute;width:${maxDim}px;height:${maxDim}px;cursor:pointer`;
  el.innerHTML = svg;
  const svgEl = el.querySelector('svg');
  if (svgEl)
    (svgEl as SVGElement).style.cssText +=
      ';position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);transform-origin:center';
  return { el, noRotate };
}
